import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Policies, Policy, RuleActions } from '@shared/models/site';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  PristineChangeEvent,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { toCidr } from '@shared/utils/data-utils';
import _ from 'lodash';
import { isValidIpOrCidr, isValidPath } from '@shared/validators/validators';
import { ChallengeType, ChallengeTypes, Rule } from '@shared/services/organization-site.service';
import { ClusterConfig } from '@shared/services/organization-cluster.service';
import { FormGroupType } from '@shared/utils/formgroup-type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DirtyStateService } from '@shared/services/dirty-state/dirty-state.service';

import { splitClipboard } from '@shared/utils/clipboard';
import { CustomPagesProfileModel } from '@shared/services/custom-page-profiles/organization-custom-pages-profile.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { LabelBetaComponent } from '@shared/components/label-beta/label-beta.component';
import { HelpComponent } from '@shared/components/help/help.component';
import { NgFor } from '@angular/common';
import { FeatureFlagDirective } from '@shared/utils/feature-flag.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { ACL_CUSTOM_PAGES_PROFILE_MANAGE, AuthService } from '@shared/services/auth.service';
import { OrganizationSiteShareService } from '@shared/services/organization-site-share.service';

@Component({
  selector: 'app-rules-details',
  templateUrl: './rules-details.component.html',
  styleUrl: './rules-details.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgSelectComponent,
    SwitchComponent,
    LabelBetaComponent,
    HelpComponent,
    NgFor,
    FeatureFlagDirective,
    TranslatePipe,
  ],
})
export class RulesDetailsComponent implements OnInit, OnChanges {
  @Input({ required: true }) rule: Rule;
  @Input({ required: true }) siteName: string;
  @Input({ required: true }) priority: number;
  @Input({ required: true }) clusterConfig: Pick<ClusterConfig, 'supportsCache' | 'supportsChallenge'>;
  @Input({ required: true }) siteCacheEnabled: boolean;
  @Output() validate = new EventEmitter<Rule>();
  @Output() cancel = new EventEmitter<void>();

  formGroup: RuleFormGroup;
  paths: FormControl<string[]>;
  ipExceptions: FormControl<string[]>;
  defaultPolicy: FormControl<Policy>;
  challenge: FormControl<ChallengeType>;
  brainAnalysisEnabled: FormControl<boolean>;
  customPagesProfile: FormControl<CustomPagesProfileModel>;
  customPagesProfileModels: CustomPagesProfileModel[] = [];
  canCustomErrorProfile: boolean = false;

  constructor(
    private formBuilder: FormBuilder,
    private dirtyState: DirtyStateService,
    private auth: AuthService,
    protected organizationSiteShareService: OrganizationSiteShareService,
  ) {
    this.initFormGroup();
  }
  ngOnInit(): void {
    this.loadCustomPagesProfiles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rule']) {
      this.formGroup.patchValue({
        ..._.pick(this.rule, 'comment', 'cache', 'paths', 'defaultPolicy', 'ipExceptions', 'challenge'),
        brainAnalysisEnabled: this.rule.action === 'brain',
      });
      this.syncCustomPagesProfile();
    }
    if (changes['siteCacheEnabled']) {
      if (this.siteCacheEnabled) this.formGroup.get('cache').enable();
      else this.formGroup.get('cache').disable();
    }
  }

  private initFormGroup() {
    this.paths = new FormControl<string[]>([], [Validators.required, Validators.minLength(1)]);
    this.defaultPolicy = new FormControl<Policy>('allow');
    this.ipExceptions = new FormControl<string[]>([]);
    this.challenge = new FormControl<ChallengeType>('disabled');
    this.brainAnalysisEnabled = new FormControl<boolean>(true);
    this.customPagesProfile = new FormControl<CustomPagesProfileModel>(null);

    this.formGroup = this.formBuilder.group({
      paths: this.paths,
      brainAnalysisEnabled: this.brainAnalysisEnabled,
      defaultPolicy: this.defaultPolicy,
      ipExceptions: this.ipExceptions,
      cache: new FormControl(false),
      comment: new FormControl<string>(''),
      challenge: this.challenge,
      customPagesProfile: this.customPagesProfile,
    });

    this.formGroup.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof PristineChangeEvent) {
        this.dirtyState.setDirty();
      }
    });

    this.brainAnalysisEnabled.valueChanges.subscribe((value) => {
      if (value) this.challenge.enable();
      else this.challenge.disable();
    });
  }

  loadCustomPagesProfiles() {
    this.organizationSiteShareService
      .getAvailableCustomPagesProfile(this.siteName, { number: 0, size: 100 })
      .subscribe((page) => {
        this.customPagesProfileModels = page.content;
        this.canCustomErrorProfile = page.content.length > 0 && this.auth.userHasFeature(ACL_CUSTOM_PAGES_PROFILE_MANAGE);
        this.syncCustomPagesProfile();
      });
  }

  private syncCustomPagesProfile() {
    const uid = this.rule?.customPagesProfile?.uid;
    const match = this.customPagesProfileModels.find((m) => m.uid === uid) ?? null;
    this.customPagesProfile.setValue(match);
  }

  public emitValidate(): void {
    this.validate.emit({
      ...this.rule,
      ...this.toRule(this.formGroup.getRawValue()),
    });
  }

  private toRule(formData: RuleFormData): Omit<Rule, 'active'> {
    const { brainAnalysisEnabled, ...other } = formData;
    return { ...other, action: brainAnalysisEnabled ? 'brain' : 'bypass' };
  }

  public emitCancel(): void {
    this.cancel.emit();
  }

  backToList() {
    if (this.formGroup.invalid) this.emitCancel();
    else this.emitValidate();
  }

  protected readonly RuleActions = RuleActions;

  protected onPasteIps(event: ClipboardEvent) {
    this.addIps(splitClipboard(event));
  }

  protected onPastePaths(event: ClipboardEvent) {
    this.addPaths(splitClipboard(event));
  }

  protected addIps(ips: string[]) {
    const values = ips.filter(isValidIpOrCidr).map(toCidr);
    this.ipExceptions.setValue(Array.from(new Set<string>([...this.ipExceptions.getRawValue(), ...values])));
  }

  protected addPaths(paths: string[]) {
    const values = paths.filter(isValidPath);
    this.paths.setValue(Array.from(new Set<string>([...this.paths.getRawValue(), ...values])));
  }

  @HostListener('window:keydown', ['$event'])
  protected onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.emitCancel();
    } else if (event.key === 'Enter' && event.ctrlKey && this.formGroup.valid) {
      this.emitValidate();
    }
  }

  protected addIp(ip: string) {
    if (!isValidIpOrCidr(ip)) return false;

    return toCidr(ip);
  }

  protected addPath(path: string) {
    if (!isValidPath(path)) return false;

    return path;
  }

  protected onAddIp(ip: any) {
    const dedupedIps = new Set<string>([...this.ipExceptions.getRawValue()]);
    this.ipExceptions.setValue(Array.from(dedupedIps));
  }

  protected onAddPath(path: any) {
    const dedupedPaths = new Set<string>([...this.paths.getRawValue()]);
    this.paths.setValue(Array.from(dedupedPaths));
  }

  protected readonly Policies = Policies;
  protected readonly ChallengeTypes = ChallengeTypes;
}

type RuleFormData = Omit<Rule, 'id' | 'active' | 'action'> & { brainAnalysisEnabled: boolean };

type RuleFormGroup = FormGroupType<RuleFormData>;
